import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import {
  BarChart3,
  Briefcase,
  FileText,
  Target,
  Database,
  Filter,
  TrendingUp,
  Layers,
  Zap,
  ChevronRight,
} from "lucide-react";
import { usePageMeta } from "@/hooks/use-page-meta";
import { useJsonLd } from "@/hooks/use-json-ld";
import { buildBreadcrumbList } from "@/lib/breadcrumbs";

const solutions = [
  {
    icon: Briefcase,
    title: "Portfolio ESG analytics",
    body: "Upload portfolios and instantly see weighted ESG scores, pillar breakdowns, sector exposure, and country tilts. Compare against custom benchmarks or our regional indices.",
  },
  {
    icon: Filter,
    title: "Negative & positive screening",
    body: "Build screening rules across 43 criteria — exclude controversies, filter on minimum scores, or tilt toward best-in-class issuers within each sector.",
  },
  {
    icon: BarChart3,
    title: "Sector & peer benchmarking",
    body: "Compare any company against its sector and country peers. Identify ESG outliers and emerging leaders before consensus reprices them.",
  },
  {
    icon: TrendingUp,
    title: "Engagement support",
    body: "Use criterion-level breakdowns to design issuer engagement programs. Track score progression over time as companies disclose more.",
  },
  {
    icon: FileText,
    title: "Custom reporting",
    body: "Generate branded PDF reports for clients, IC committees, or regulators. Export aggregate ESG metrics in formats compatible with PRI and SFDR templates.",
  },
  {
    icon: Database,
    title: "Data integration",
    body: "Pull ratings into your OMS, risk system, or research workflow via CSV exports and structured downloads. Bulk access for quant teams.",
  },
];

const audiences = [
  { label: "Asset managers", body: "Integrate ESG into your investment process across discretionary and quant strategies." },
  { label: "Sovereign wealth funds", body: "Apply consistent ESG criteria across regional equity allocations and direct holdings." },
  { label: "Family offices", body: "Align portfolio holdings with values-based mandates without sacrificing transparency." },
  { label: "Banks & private banks", body: "Equip relationship managers with ESG insights for HNW client conversations." },
  { label: "Corporate IR teams", body: "Benchmark your disclosures against regional peers and identify reporting gaps." },
];

const InvestorSolutions = () => {
  usePageMeta({
    title: "ESG Investor Solutions — Portfolio Analytics & Screening | ESG Invest",
    description:
      "Portfolio ESG analytics, screening, peer benchmarking, and reporting tools built for asset managers and institutional investors operating across Middle East markets.",
    path: "/investor-solutions",
  });

  useJsonLd(
    "ld-solutions-breadcrumbs",
    buildBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "Investor Solutions", path: "/investor-solutions" },
    ]),
  );

  return (
    <Layout>
      <div className="min-h-screen bg-se-gray-50 dark:bg-background">
        <section className="bg-gradient-to-br from-se-navy to-se-green text-white">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
            <nav className="flex items-center gap-2 text-sm text-white/70 mb-6" aria-label="Breadcrumb">
              <Link href="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white">Investor Solutions</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 max-w-3xl">
              ESG tools built for Middle East investors
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl leading-relaxed">
              Portfolio analytics, screening, and reporting workflows powered by independent ESG
              ratings on 880+ listed companies across the GCC and broader Middle East.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild variant="accent" size="lg">
                <Link href="/contact">Request a trial</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                <Link href="/subscriptions">View subscriptions</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map((s) => (
              <Card key={s.title} className="p-6 h-full">
                <div className="w-12 h-12 rounded-lg bg-se-green-lighter dark:bg-se-green/10 flex items-center justify-center mb-4">
                  <s.icon className="w-6 h-6 text-se-green" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{s.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{s.body}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="bg-white dark:bg-card">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
            <div className="grid lg:grid-cols-3 gap-10">
              <div>
                <Layers className="w-10 h-10 text-se-green mb-4" />
                <h2 className="text-3xl font-bold text-se-navy dark:text-foreground mb-4">
                  One dataset. Every workflow.
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  ESG Invest replaces a patchwork of regional spreadsheets, broker reports, and
                  global datasets with a single source of truth — purpose-built for Middle East
                  coverage and integrated into the tools investment teams already use.
                </p>
              </div>
              <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
                {audiences.map((a) => (
                  <div key={a.label} className="p-5 rounded-lg border border-border">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-se-green" />
                      <p className="font-semibold">{a.label}</p>
                    </div>
                    <p className="text-sm text-muted-foreground">{a.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <Card className="p-8 md:p-12 bg-gradient-to-br from-se-navy to-se-green text-white">
            <div className="md:flex md:items-center md:justify-between gap-6">
              <div>
                <Zap className="w-8 h-8 text-white mb-3" />
                <h2 className="text-2xl md:text-3xl font-bold mb-2">Ready to see your portfolio scored?</h2>
                <p className="text-white/90 max-w-2xl">
                  Get a free portfolio analysis and a trial of the ESG Invest platform — including
                  full company profiles, screening, and benchmarking tools.
                </p>
              </div>
              <Button asChild variant="accent" size="lg" className="mt-4 md:mt-0 shrink-0">
                <Link href="/contact">Request access</Link>
              </Button>
            </div>
          </Card>
        </section>
      </div>
    </Layout>
  );
};

export default InvestorSolutions;
